import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import ModalFrame from '../../primitives/ModalFrame';
import { getMapObjectTypesAdminMapObjectsTypesGet } from '../../../client';
import { getDecorImagePath } from '../../../data/mapDecorAssets';


export type MapObjectType = string;

interface MapObjectTypeSelectionModalProps {
  isOpen: boolean;
  onSelectType: (type: MapObjectType) => void;
  onClose: () => void;
}

const ISLAND_TYPE_TO_IMAGE: Record<string, number> = {
  island_paradise: 0,
  island_coral: 1,
  island_lagoon: 2,
  island_opal: 3,
  island_jade: 4,
};

const getImagePath = (type: string): string => {
  if (type === 'plot') {
    return '/game-assets/map-objects/plot.webp';
  } else if (type === 'parking_spot') {
    return '/game-assets/map-objects/parking_spot.webp';
  } else if (type.startsWith('island_')) {
    const idx = ISLAND_TYPE_TO_IMAGE[type] ?? 0;
    return `/game-assets/islands/${idx}.webp`;
  } else if (type.startsWith('decor_')) {
    return getDecorImagePath(type);
  } else {
    return `/game-assets/map-objects/decorations/${type}.webp`;
  }
};


export default function MapObjectTypeSelectionModal({
  isOpen,
  onSelectType,
  onClose,
}: MapObjectTypeSelectionModalProps) {
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [isClickable, setIsClickable] = useState(false);
  const { data: types, isLoading } = useQuery({
    queryKey: ['mapObjectTypes'],
    queryFn: async () => {
      const { data } = await getMapObjectTypesAdminMapObjectsTypesGet({ throwOnError: true });
      return data.types;
    },
    enabled: isOpen,
  });

  const [wasOpen, setWasOpen] = useState(isOpen);
  // (b) Block clicks immediately when open state flips — during render
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    setIsClickable(false);
  }

  // (c) Timer unlocks clicks after open — setState only in timeout callback
  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => {
      setIsClickable(true);
    }, 200);
    return () => clearTimeout(timer);
  }, [isOpen]);

  if (!isOpen) return null;

  const handleTypeClick = (type: string) => {
    if (!isClickable) return; // Блокуємо кліки протягом затримки
    setSelectedType(type);
  };

  const handleConfirm = () => {
    if (selectedType) {
      onSelectType(selectedType);
      setSelectedType(null);
    }
  };

  const handleCancel = () => {
    setSelectedType(null);
  };

  const handleClose = () => {
    setSelectedType(null);
    onClose();
  };

  return (
    <ModalFrame
      isOpen={isOpen}
      onClose={handleClose}
      variant="dialog"
      className="max-w-[372px] rounded-xl bg-object-picker-surface p-0"
      showBackButton={true}
      title={selectedType ? "Confirm creation" : "Select object type"}
      headerClassName="bg-[image:var(--gradient-object-picker-header)]"
    >
      <div className="relative w-full">
        <div
          className="map-object-type-modal-content mx-auto box-border flex w-full max-w-[332px] flex-col items-center gap-xl px-2xl py-3xl max-[360px]:gap-lg max-[360px]:px-lg max-[360px]:py-xl"
        >
          {selectedType ? (
            <>
              {/* Підтвердження */}
              <div className="flex w-full flex-col items-center gap-lg">
                  {selectedType && (
                  <div className="flex h-[120px] w-full max-w-[120px] items-center justify-center overflow-hidden">
                    <img
                      src={getImagePath(selectedType)}
                      alt={selectedType}
                      className="max-h-full max-w-full object-contain"
                      onError={(e) => {
                        const img = e.target as HTMLImageElement;
                        img.style.display = 'none';
                        const container = img.parentElement;
                        if (container && !container.querySelector('.no-image-placeholder')) {
                          const placeholder = document.createElement('div');
                          placeholder.className = 'no-image-placeholder';
                          placeholder.innerHTML = `
                            <div style="text-align: center; color: var(--color-object-picker-placeholder); font-family: 'Lato', sans-serif; font-size: 10px; margin-bottom: 4px; line-height: 1.2;">NO IMAGE</div>
                            <pre style="margin: 0; font-family: monospace; font-size: 8px; line-height: 1.1; text-align: center; color: var(--color-object-picker-placeholder);">(>_&lt;)<br/>  ┌─┐<br/>  │ │<br/>  └─┘</pre>
                          `;
                          placeholder.style.cssText = `
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            justify-content: center;
                            width: 100%;
                            height: 100%;
                            box-sizing: border-box;
                            padding: 4px;
                            color: var(--color-object-picker-placeholder);
                            border: 1px dashed var(--color-text-muted);
                            border-radius: 8px;
                            background: var(--color-object-picker-item-surface);
                            overflow: hidden;
                          `;
                          container.appendChild(placeholder);
                        }
                      }}
                    />
                  </div>
                )}
                <div className="w-full text-center [font-family:Lato,sans-serif] text-[14px] leading-[20px] text-text-soft">
                  Create object of type: <strong className="text-white">{selectedType}</strong>?
                </div>
              </div>
              <div className="flex w-full gap-lg">
                <button
                  onClick={handleCancel}
                  className="flex-1 cursor-pointer rounded-md border-2 border-object-picker-item-border bg-object-picker-item-surface px-2xl py-lg [font-family:Lato,sans-serif] text-[16px] text-white"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--color-text-muted)';
                    e.currentTarget.style.background = 'var(--color-object-picker-item-hover)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--color-object-picker-item-border)';
                    e.currentTarget.style.background = 'var(--color-object-picker-item-surface)';
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirm}
                  className="flex-1 cursor-pointer rounded-md border-none bg-[image:var(--gradient-onboarding-action)] px-2xl py-lg [font-family:Lato,sans-serif] text-[16px] text-white"
                >
                  Confirm
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Список типів об'єктів */}
              <div className="flex w-full flex-col gap-[10px]">
                {isLoading ? (
                  <div className="p-2xl text-center [font-family:Lato,sans-serif] text-[16px] text-text-soft">
                    Loading...
                  </div>
                ) : (
                  [...(types || [])].sort((a, b) => {
                    // plot завжди на першому місці
                    if (a === 'plot') return -1;
                    if (b === 'plot') return 1;
                    // parking_spot завжди на другому місці
                    if (a === 'parking_spot') return -1;
                    if (b === 'parking_spot') return 1;
                    return a.localeCompare(b);
                  }).map((type) => (
                    <button
                      key={type}
                      className="map-object-type-button box-border flex w-full flex-col items-center justify-center gap-md rounded-md border-2 border-object-picker-item-border bg-object-picker-item-surface p-lg text-white max-[360px]:gap-sm max-[360px]:p-md"
                      onClick={() => handleTypeClick(type)}
                      disabled={!isClickable}
                      style={{
                        cursor: isClickable ? 'pointer' : 'not-allowed',
                      }}
                      onMouseEnter={(e) => {
                        if (isClickable) {
                          e.currentTarget.style.borderColor = 'var(--color-onboarding-action)';
                          e.currentTarget.style.background = 'var(--color-object-picker-item-hover)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'var(--color-object-picker-item-border)';
                        e.currentTarget.style.background = 'var(--color-object-picker-item-surface)';
                      }}
                    >
                      <div className="flex h-20 w-full max-w-20 items-center justify-center overflow-hidden">
                        <img
                          src={getImagePath(type)}
                          alt={type}
                          className="max-h-full max-w-full object-contain"
                          onError={(e) => {
                            // Якщо зображення не знайдено, замінюємо на ASCII art
                            const img = e.target as HTMLImageElement;
                            img.style.display = 'none';
                            const container = img.parentElement;
                            if (container && !container.querySelector('.no-image-placeholder')) {
                              const placeholder = document.createElement('div');
                              placeholder.className = 'no-image-placeholder';
                              placeholder.innerHTML = `
                                <div style="text-align: center; color: var(--color-object-picker-placeholder); font-family: 'Lato', sans-serif; font-size: 10px; margin-bottom: 4px; line-height: 1.2;">NO IMAGE</div>
                                <pre style="margin: 0; font-family: monospace; font-size: 8px; line-height: 1.1; text-align: center; color: var(--color-object-picker-placeholder);">(>_&lt;)<br/>  ┌─┐<br/>  │ │<br/>  └─┘</pre>
                              `;
                              placeholder.style.cssText = `
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            justify-content: center;
                            width: 100%;
                            height: 100%;
                            max-width: 100%;
                            max-height: 100%;
                            box-sizing: border-box;
                            padding: 4px;
                            color: var(--color-object-picker-placeholder);
                            border: 1px dashed var(--color-text-muted);
                            border-radius: 8px;
                            background: var(--color-object-picker-item-surface);
                            overflow: hidden;
                          `;
                              container.appendChild(placeholder);
                            }
                          }}
                        />
                      </div>
                      <span
                        className="w-full text-center [font-family:Lato,sans-serif] text-[12px] leading-[16px] max-[360px]:text-[11px]"
                      >
                        {type}
                      </span>
                    </button>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </ModalFrame>
  );
}
